// @ts-strict-ignore
import { AfterViewInit, ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { EventEmitter, Output } from '@angular/core';
import { LMFormManualRepService } from '@form/lm-form-manual-rep.service';

import { ActivatedRoute } from '@angular/router';
import { LMFieldManualRepService } from '../services';
import { LMModelChangedEvent } from '@form/model';
import { BehaviorSubject, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { LMHtmlUrlDocComponent, LMUrlDocSaveResult } from '@components/html';
import { LMHtmlUrlDocService } from '@services/lm-html-url-doc.service';
import { LMContextService, LMLoggerService } from '@app/services';

enum LMManualRepTypeDisplayEnum {
  READ_ONLY = 0,
  PLAIN
}

@Component({
  selector:    'lm-manual-representation',
  templateUrl: './lm-manual-representation.component.html',
  styleUrls:   [ './lm-manual-representation.component.scss' ],
  providers: [
    LMFormManualRepService,
    LMFieldManualRepService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMManualRepresentationComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(forwardRef(() => LMHtmlUrlDocComponent)) private lmHtmlUrlDocComponent;

  @Input() set manualRep(manualRep: LMFormLayoutManualRep) {
    this._manualRep = Object.assign({}, manualRep);
  }

  @Output() contentModified = new EventEmitter<any>();

  public automationId: string;
  public frameIdentifier = '';
  public bypassmanualRepUpdate = false;
  public _manualRep: LMFormLayoutManualRep;
  public manualRefUrl: string;
  public displayType: LMManualRepTypeDisplayEnum;
  public layoutDisplayType;
  protected subscriptionArray: Array<ISubscription> = [];

  constructor(
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private route: ActivatedRoute,
    private lmLoggerService: LMLoggerService,
    protected lmFormManualRepService: LMFormManualRepService,
    private lmHtmlUrlDocService: LMHtmlUrlDocService,
    protected lmContextService: LMContextService,
  ) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMManualRepTypeDisplayEnum;
    this.frameIdentifier = this.lmHtmlUrlDocService.createFrameIdentifier();
  }

  ngOnInit() {
    super.ngOnInit();

    this.automationId = this.buildAutomationId();

    const dataArea = this.lmContextService.getContext().urlPdlOrDa;
    const webApp = this.lmContextService.getContext().webApp;

    this.manualRefUrl = window.location.protocol + '//' + window.location.host.toLowerCase() + '/' + dataArea + '/' + webApp
            + '/manualrep/' + this._manualRep.repName + '?manualRepType=' + this._manualRep.type;  //manualRepName=' + this._manualRep.repName + '&

    if (this.isReadOnly) {
      this.displayType = this.layoutDisplayType.READ_ONLY;

    } else {
      this.displayType = this.layoutDisplayType.PLAIN;

    }

    if (this.frameIdentifier.length > 0) {
      this.lmHtmlUrlDocService.setManualFields(this.frameIdentifier, this.getManualRepFields(), this);
    }
    this.model.addManualRepField(this._manualRep.fieldId);
  }

  private buildAutomationId() {
    if (!this._manualRep.repName) {
      return null;
    }

    const formNamePrefix = this.lmFieldServiceAdapter.lmFormSpecUtilService.formNamePrefix(this.model.formSpec);
    const formName = this.lmFieldServiceAdapter.lmFormSpecUtilService.formName(this.model.formSpec);
    // const instance = (this.field.hasOwnProperty('inst') && this.field.inst > 0) ? this.field.inst.toString() : undefined;
    return this.lmFieldServiceAdapter.lmUniqueIdService.uniqueAutomationId([ formNamePrefix, formName, this._manualRep.repName]);
  }

  ngAfterViewInit() {
    // super.ngAfterViewInit();
    this.subscriptionArray.push(this.model.modelChanged.subscribe((modelChangedEvent: LMModelChangedEvent) => {
      this.lmHtmlUrlDocService.setManualFields(this.frameIdentifier, this.getManualRepFields(), this);

      // When the model is updated using the landmark-api setModelField has a param to notify listeners
      //         updateModelFromManualRepField(fieldId: string, value: any, notifyListeners?: boolean)
      // when that is true the ModelEventChanged is set with the value of 'manualRepChanged' insyead of 'update'
      //         this._modelChanged$.next(new LMModelChangedEvent('manualRepChanged'));
      // We then skip the reload of the htmlurlDoc which will block the event from being forwarded to the html page.
      // At the same time we also set a short timer to hold that value o cover the fact that the field change could trigger
      // when value changes in the lpl that will trigger chnages in other fields that we would also like t block the event
      // from being fired to the html for thise field also since they are a result of the manual rep field change
      if (!this.bypassmanualRepUpdate && modelChangedEvent.eventType !== 'manualRepChanged') {
        this.lmHtmlUrlDocComponent.reload();
      } else {
        if (!this.bypassmanualRepUpdate) {
          this.lmLoggerService.log('Manual Rep Model Modify bypass enabled');
          this.bypassmanualRepUpdate = true;
          setTimeout(() => {
            this.bypassmanualRepUpdate = false;
            this.lmLoggerService.log('Manual Rep Model Modify bypass disabled');
          }, 1000);
        }

      }
    }));
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    // clean up all other subscriptions
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
    this.subscriptionArray = [];
  }

  onContentModified(event: LMListFieldChangedEvent) {
    this.contentModified.emit(event);
  }

  onSetFieldDirty(dirty: boolean) {
    this.isDirty = dirty;
    this.model.setDirty(this._manualRep.fieldId, this.isDirty);
  }

  onCustomDocumentLoaded() {
  }

  onBeforeOpen() {
  }

  public runSave(): BehaviorSubject<LMUrlDocSaveResult> {
    return this.lmHtmlUrlDocComponent.runSave();
  }

  public getLmHtmlUrlDocComponent()  {
    return this.lmHtmlUrlDocComponent;
  }

  public setModelFields(fieldId: string, value: any, notifyListeners?: boolean){

    this.model.updateModelFromManualRepField(fieldId, value, notifyListeners);
    const modelField = this.model.getField(fieldId);

    // If the field being updated has initial values we fire a change event on that field to force
    // the rules to be run similar to when a value is selected for a select field.
    if (modelField.fireInitialValue) {
      this.fireInitialValueRuleEvent(fieldId);
    }

    if (notifyListeners) {
      this.lmHtmlUrlDocComponent.reload();
    }
  }

  public getFormState(): LMAPIFormState {
    // this will currently return just the readonly value but could be used in the future to expand the information when it is needed.
    return { readonly: this.model.isReadOnlyForm };
  }

  public getModelSubject() :Subject<LMModelChangedEvent> {
    return this.model.modelChanged;
  }

  public getManualRepFields() {
    const retObj = {};
    const rep: LMFormLayoutManualRep = this._manualRep as LMFormLayoutManualRep;
    const fields: Array<LMManualRepMappingField> = rep.fieldMappings;
    let value: LMRelatedValueSpec;
    for (const field of fields) {
      if (field.type === 'text') {
        value = field.value;
      } else {
        const modelField = this.model.getField(field.fieldId);
        value = modelField.value;
      }
      retObj[field.property] = value;
    }
    return retObj;
  }
}
